/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getGroup} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Entity, Link} from '@ion/core/src/types/entities';
import {hasGroupInherited} from '@ion/core/src/utils/DeviceUtils';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {useIdentifierResults} from '@ion/dls/src/hooks/useIdentifierResults';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {handleColumnError} from '../../utils/errorMessages';
import {viewGroup} from '../admin/groups/state';
import DeviceGroupInheritableTitle from './DeviceGroupInheritableTitle';
import IdentifierDialog from './IdentifierDialog';

export interface DeviceGroupInheritableLinksType {
  item: GridResult;
};

const groupsName = {
  lineHeight     : '20px',
  justifyContent : 'center',
  borderRadius   : '2px',
  padding        : '0px 6px',
  margin         : '4px'
};

const groupSections = {
  alignItems     : 'center',
  cursor         : 'pointer',
  width          : '100%',
  justifyContent : 'left',
  fontSize       : '10px',
  fontWeight     : 500
};

const useStyles = makeStyles(() => ({
  groupSection : {
    ...groupSections,
    display : 'inline-flex'
  },
  groupSectionTitle : {
    ...groupSections,
    display : 'flex'
  },
  groupName : {
    ...groupsName,
    backgroundColor : 'rgba(224, 224, 224, 1)'
  },
  inheritedGroupName : {
    ...groupsName,
    backgroundColor : 'rgba(2, 171, 202, 0.3)'
  },
  moreGroups : {
    color  : dlsColor('grey3'),
    cursor : 'pointer'
  }
}));

/**
 * Defines the DeviceGroupInheritableLinks component. This component is used to
 * display the group names with links when we hover on groups column.
 *
 * ```typescript
 *
 *   import DeviceGroupInheritableLinks from './DeviceGroupInheritableLinks';
 *
 *   <IonGrid  columnComponents={{
 *     groupsUI : DeviceGroupInheritableLinks
 *   }} />
 *
 * ```
 */
export const DeviceGroupInheritableLinks = ({item}: DeviceGroupInheritableLinksType) => {
  const dispatch                      = useDispatch();
  const classes                       = useStyles();
  const [openGroups, setOpenGroups]   = useState(false);
  const [columnWidth, setColumnWidth] = useState(0);
  const {
    data: {groups = {}, name = {}} = {},
    api
  } = item || {};

  useEffect(() => {
    const calculateVisibleGroups = () => {
      const columnWidths = api?.getColumnState()?.find(column => column?.colId === 'groupList')?.width;
      if (columnWidths) {
        setColumnWidth(columnWidths);
      }
    };
    calculateVisibleGroups();
    api.addEventListener('columnResized', calculateVisibleGroups);
    return () => {
      api.removeEventListener('columnResized', calculateVisibleGroups);
    };
  }, [api]);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenGroups(false);
      } else {
        setOpenGroups(true);
      }
    };
  }, [openGroups]);

  const onGroupClick = useCallback((group: Link) => {
    toggleTooltip();
    (async function() {
      try {
        const groupData = await getGroup(entityId(group?.href));
        dispatch(viewGroup(groupData));
      } catch (err) {
        error(err);
        dispatch(handleColumnError('Entity', 'Group'));
      }
    })();
  }, []);

  const onTitleClick = useCallback((group: Link) => (e: MouseEvent<HTMLDivElement>) => {
    onGroupClick(group);
  }, []);

  const getGroupsTotalWidth = useCallback(() => {
    return groups?.reduce?.((total, group) => total + group?.title?.length, 0);
  }, [groups]);

  const getDisplayedGroups = useCallback(() => {
    let currentWidth      = 0;
    const paddingWidth    = 12;
    const marginWidth     = 4;
    const titleWidth      = 7;
    const displayedGroups = [];
    const remainingGroups = [];

    const totalGroupsWidth = getGroupsTotalWidth() * titleWidth;

    if (totalGroupsWidth <= columnWidth) {
      return {
        displayedGroups : groups,
        remainingGroups : []
      };
    }

    groups?.forEach?.(group => {
      const groupTitleWidth = group?.title?.length * titleWidth + paddingWidth + marginWidth;

      if (currentWidth + groupTitleWidth > columnWidth && displayedGroups?.length) {
        remainingGroups.push(group);
      } else {
        currentWidth += groupTitleWidth;
        displayedGroups.push(group);
      } if (displayedGroups?.length === 0 && groups?.length) {
        displayedGroups.push(groups[0]);
        remainingGroups?.splice(0, 1);
      }
    });

    return {
      displayedGroups,
      remainingGroups
    };
  }, [columnWidth, groups]);

  const {
    displayedGroups,
    remainingGroups
  } = getDisplayedGroups() || {};

  const {
    itemToDisplay,
    setShowDialog,
    itemLength,
    showDialog,
    moreItem
  } = useIdentifierResults({
    data           : groups,
    displayedItems : displayedGroups?.length
  }) || {};

  const moreGroupsText = useMemo(() => {
    return remainingGroups?.length >= 1 ? `${translate('admin-devices:grid.inheritableGroups', {groupLength: remainingGroups?.length})}` : '';
  }, [remainingGroups]);

  if (groups) {
    return (
      <>
        <ClickAwayListener onClickAway={toggleTooltip()}
          mountListener>
          <Tooltip arrow
            open={openGroups}
            onClose={toggleTooltip()}
            interactive={true}
            title={
              <DeviceGroupInheritableTitle groups={itemToDisplay}
                groupLength={remainingGroups?.length}
                groupSections={classes.groupSectionTitle}
                setShowDialog={setShowDialog}
                itemLength={itemLength}
                moreItem={moreItem}
                onGroupClick={onGroupClick} />
            }
            placement='bottom'>
            <div className={classes.groupSection}>
              {displayedGroups?.map?.((group, index) => {
                return (
                  <div key={index}
                    className={hasGroupInherited(group) ? classes.inheritedGroupName : classes.groupName}
                    onClick={onTitleClick(group)}>
                    {group?.title}
                  </div>
                );
              })}
              {remainingGroups?.length >= 1 && (
                <div className={classes.moreGroups}
                  onClick={toggleTooltip(true)}>
                    &nbsp;<span>{moreGroupsText}</span>
                </div>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
        <Render.If condition={showDialog}>
          <IdentifierDialog setShowDialog={setShowDialog}
            onTitleClick={onTitleClick}
            showDialog={showDialog}
            deviceName={name}
            entity={Entity.group}
            data={groups} />
        </Render.If>
      </>
    );
  }
  return <div/>;
};

export default DeviceGroupInheritableLinks;
